/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {StatefulComponent} from '../stateful/stateful.component';
import {InfoChartConfigurationModel} from './models/info-chart-configuration.model';
import {InfoChartState} from './models/info-chart.state';
import {InfoChartDataLineModel} from './models/info-chart-data-line.model';
import {CurrencyBaseContract} from '../../settlements/api/contracts/currency-base.contract';
import {formatWithCurrencySymbol} from '../../settlements/shared/util/financial-number/financial-number-helper';
import {InfoChartStatusEnum} from './models/info-chart-status.enum';
import {TableLineModel} from '../table/models/table-line.model';

@Component({
    selector: 'app-info-chart',
    templateUrl: './info-chart.component.html',
    styleUrls: ['./info-chart.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InfoChartComponent<T> extends StatefulComponent<InfoChartState<T>> implements AfterViewInit {
    @Input() configuration: InfoChartConfigurationModel<T>;
    @Input() elementClickManually: TableLineModel<T, T[]>;
    @Input() elementMouseHover: string;
    @Input() infoChartElementClosed: string;
    @ViewChild('line', {static: true}) line: ElementRef;
    @Output() identifier = new EventEmitter<T>();
    @Output() mouseHoverLineTable = new EventEmitter<T>();

    private readonly maxWidthTextDescription = 81;
    private readonly maxWidthValueToZebraChart = 16;
    private readonly minWidthZebraChart = 16;

    constructor(private changeDetectorRef: ChangeDetectorRef) {
        super();
    }

    get hasTitleSection(): boolean {
        return this.configuration?.description != null && this.configuration?.totalValue != null;
    }

    get disableTooltip(): boolean {
        return this.configuration?.totalValue === 0;
    }

    /**
     * All elements are on CLOSED status
     */
    get allInfoChartDataClosed(): boolean {
        return this?.configuration?.infoData?.filter((item) =>
                item.status.includes(InfoChartStatusEnum.CLOSED)).length === this.configuration.infoData.length;
    }

    @HostListener('window:resize')
    onResize(): void {
        this.getInfoChartGridLine();
    }

    ngAfterViewInit(): void {
        this.getInfoChartGridLine();
        this.changeDetectorRef.detectChanges();
    }

    getInfoChartGridLine(): void {
        this.getInfoChartData(this.configuration?.infoData);

        // Sum of all elements infoChartDataLine chart and zebra chart
        const dataLine = this.state.infoChartDataLine?.reduce((sum, current) => sum + current.value, 0);
        const dataZebraLines = this.state.infoChartDataZebra?.reduce((sum, current) => sum + current.value, 0);

        // Calc width and percentages
        let zebraDataLineWidth = dataZebraLines / this.configuration?.totalValue * this.line.nativeElement.offsetWidth;
        zebraDataLineWidth = (zebraDataLineWidth < 1 ? this.minWidthZebraChart : zebraDataLineWidth);
        const lineDataWidth = (dataLine / this.configuration?.totalValue * this.line.nativeElement.offsetWidth) - zebraDataLineWidth;
        const percentageZebraDataLinePercentage = (dataZebraLines / this.configuration?.totalValue) * 100;

        this.storeState({
            lineWidth: lineDataWidth,
            zebraWidth: zebraDataLineWidth,
            zebraPercentage: percentageZebraDataLinePercentage
        });
        this.getInfoChartDataLineModel(this.state.infoChartDataLine, this.configuration?.totalValue);
    }

    emitIdentifier(infoData: InfoChartDataLineModel<T>): void {
        if (infoData.value > 0) {
            this.identifier.emit(infoData.identifier);
            this.state.infoChartDataLine?.forEach((item) => item.statusLineIsActive = false);
            const identifier = this.state.infoChartDataLine?.find((item) => item.identifier === infoData.identifier);
            if (identifier != null) {
                identifier.statusLineIsActive = true;
            }
        }
    }

    clickManuallyTable(): void {
        this.state.infoChartDataLine?.forEach((item) => item.statusLineIsActive = false);
        const manualTableClick = this.state.infoChartDataLine?.find((item) =>
                `${item.identifier}` === this.elementClickManually?.identifier);
        if (manualTableClick != null && this.elementClickManually.isOpen === true) {
            manualTableClick.statusLineIsActive = true;
        }
    }

    mouseHover(identifier: T): void {
        this.mouseHoverLineTable.emit(identifier);
    }

    gridLineAndZebraStyleFormatter(): Record<string, string> {
        return {'grid-template-columns': `${this.state.lineWidth}px ${this.state.zebraWidth}px`};
    }

    /**
     * Get grid style formatter
     */
    gridLineStyleFormatter(data: InfoChartDataLineModel<T>[]): Record<string, string> {
        const dataZebraLines = this.state.infoChartDataZebra?.reduce((sum, current) => sum + current.value, 0);
        const gridTemplate = data?.map((item) =>
                `${item.value / (this.configuration.totalValue - dataZebraLines) * this.state.lineWidth}px`)
                .join(' ');
        return {'grid-template-columns': gridTemplate};
    }

    /**
     * Get status color
     */
    getStatusColor(infoData: InfoChartDataLineModel<T>): string {
        let statusEnum;
        if (this.elementClickManually != null ) {
            this.clickManuallyTable();
        }

        if (infoData?.statusLineIsActive === true) {
            statusEnum = InfoChartStatusEnum.ACTIVE;
        } else if (`${infoData?.identifier}` === this.infoChartElementClosed) {
                statusEnum = InfoChartStatusEnum.CLOSED;
        } else if (`${infoData?.identifier}` === this.elementMouseHover) {
            statusEnum = InfoChartStatusEnum.MOUSE_HOVER;
        } else if (infoData?.status != null) {
            statusEnum = infoData.status;
        } else {
            statusEnum = `${InfoChartStatusEnum.UNKNOWN} info-chart-line-item-unknown`;
        }
        return statusEnum;
    }

    getStatusDataCy(index: number): string {
        let label = 'info-chart-status';
        if (index != null) {
            label += `-${index}`;
        }
        return label;
    }

    /**
     * Get currency formatter
     */
    getCurrencyFormatter(value: number, currency: CurrencyBaseContract): string {
        if (value !== this.state.lineWidth && value > 0) {
            return formatWithCurrencySymbol(value, currency?.alphabeticCode);
        }
    }

    tooltipText(infoData: InfoChartDataLineModel<T>, tooltip: string): string {
        if (infoData?.statusLineIsActive === true) {
            return `Retract ${tooltip}`;
        } else {
            return `Expand ${tooltip}`;
        }
    }

    /**
     * Mapper of InfoChartDataLineModel if it is intermidialState or not and store the infoChartDataLine
     */
    private getInfoChartDataLineModel(infoChartData: InfoChartDataLineModel<T>[], totalAmount: number): void {
        const infoChartDataElements: InfoChartDataLineModel<T>[] = [];
        if (infoChartData == null) {
            infoChartDataElements.push({value: this.state.lineWidth} as InfoChartDataLineModel<T>);
        } else {
            infoChartData.forEach((infoData) => {
                const totalValuePixels = (infoData.value / totalAmount) * this.state.lineWidth;
                const totalValuePercentage = infoData.value / totalAmount * 100;

                const infoChartDataElement: InfoChartDataLineModel<T> = {
                    intermediateState: false,
                    identifier: infoData.identifier,
                    value: infoData.value,
                    status: infoData.status,
                    percentage: totalValuePercentage,
                    description: infoData.description,
                    statusLineIsActive: false
                };

                if (totalValuePixels < this.maxWidthTextDescription) {
                    infoChartDataElements.push({...infoChartDataElement, intermediateState: true});
                } else {
                    infoChartDataElements.push({...infoChartDataElement});
                }
            });
        }
        this.storeState({infoChartDataLine: infoChartDataElements});
    }

    /**
     * Store the infoChartDataZebra and infoChartDataLine according max width value (maxWidthValueToZebraChart)
     */
    private getInfoChartData(data: InfoChartDataLineModel<T>[]): void {
        const zebraBars: InfoChartDataLineModel<T>[] = [];
        const lineBars: InfoChartDataLineModel<T>[] = [];

        if (data?.length > 0) {
            data.map(((item) => {
                const lineData = item.value / this.configuration.totalValue * this.line.nativeElement.offsetWidth;
                if (lineData <= this.maxWidthValueToZebraChart) {
                    zebraBars.push(item);
                } else {
                    lineBars.push(item);
                }
            }));
            this.storeState({infoChartDataZebra: zebraBars, infoChartDataLine: lineBars});
        }
    }
}
